import React, { useState } from 'react'
import { Vote, Users, TrendingUp, Clock, CheckCircle, XCircle, AlertCircle, Calendar, DollarSign, BarChart3, Shield, Zap } from 'lucide-react'

interface DAOGovernanceProps {
  data: {
    daoName: string
    description: string
    tokenSymbol: string
    tokenPrice: string
    tokenPriceChange: number
    totalSupply: string
    treasury: {
      totalValue: string
      tokens: Array<{
        symbol: string
        amount: string
        value: string
        percentage: number
      }>
    }
    voting: {
      quorum: number
      votingPeriod: number
      minTokensToVote: number
      totalVotingPower: string
    }
    proposals: Array<{
      id: string
      title: string
      description: string
      proposer: string
      status: 'active' | 'passed' | 'rejected' | 'executed'
      startDate: string
      endDate: string
      forVotes: number
      againstVotes: number
      abstainVotes: number
      category: string
      type: 'treasury' | 'governance' | 'technical' | 'community'
    }>
    members: {
      total: number
      active: number
      newThisWeek: number
      topContributors: Array<{
        address: string
        name: string
        contributions: number
        votingPower: string
      }>
    }
    activity: {
      proposalsThisMonth: number
      averageParticipation: number
      totalTransactions: number
    }
  }
}

export default function DAOGovernance({ data }: DAOGovernanceProps) {
  const [selectedProposal, setSelectedProposal] = useState<string | null>(null)
  const [filterStatus, setFilterStatus] = useState<string>('all')
  const [filterType, setFilterType] = useState<string>('all')

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-blue-500/20 text-blue-400 border-blue-400/30'
      case 'passed': return 'bg-green-500/20 text-green-400 border-green-400/30'
      case 'rejected': return 'bg-red-500/20 text-red-400 border-red-400/30'
      case 'executed': return 'bg-purple-500/20 text-purple-400 border-purple-400/30'
      default: return 'bg-gray-500/20 text-gray-400 border-gray-400/30'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <Clock className="w-4 h-4" />
      case 'passed': return <CheckCircle className="w-4 h-4" />
      case 'rejected': return <XCircle className="w-4 h-4" />
      case 'executed': return <Shield className="w-4 h-4" />
      default: return <AlertCircle className="w-4 h-4" />
    }
  }

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'treasury': return 'bg-yellow-500/20 text-yellow-400'
      case 'governance': return 'bg-blue-500/20 text-blue-400'
      case 'technical': return 'bg-green-500/20 text-green-400'
      case 'community': return 'bg-purple-500/20 text-purple-400'
      default: return 'bg-gray-500/20 text-gray-400'
    }
  }

  const filteredProposals = data.proposals.filter(proposal => {
    const matchesStatus = filterStatus === 'all' || proposal.status === filterStatus
    const matchesType = filterType === 'all' || proposal.type === filterType
    return matchesStatus && matchesType
  })

  const activeProposals = data.proposals.filter(p => p.status === 'active')
  const totalVotes = data.proposals.reduce((sum, p) => sum + p.forVotes + p.againstVotes + p.abstainVotes, 0)

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-indigo-900 text-white">
      {/* Header */}
      <header className="bg-black/50 backdrop-blur-sm border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-lg flex items-center justify-center">
                <Vote className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 
                  className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-indigo-400 bg-clip-text text-transparent"
                  style={{ fontFamily: 'Georgia, serif' }}
                >
                  {data.daoName}
                </h1>
                <p className="text-purple-200 text-sm">Decentralized Governance</p>
              </div>
            </div>
            
            <nav className="hidden md:flex space-x-8">
              <a href="#governance" className="text-white/80 hover:text-white transition-colors">Governance</a>
              <a href="#proposals" className="text-white/80 hover:text-white transition-colors">Proposals</a>
              <a href="#treasury" className="text-white/80 hover:text-white transition-colors">Treasury</a>
              <a href="#members" className="text-white/80 hover:text-white transition-colors">Members</a>
            </nav>
            
            <div className="flex items-center space-x-4">
              <button className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-colors">
                Connect Wallet
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 
              className="text-4xl md:text-6xl font-bold mb-6"
              style={{ fontFamily: 'Georgia, serif' }}
            >
              DAO Governance
            </h2>
            <p className="text-xl md:text-2xl text-purple-200 mb-8 max-w-3xl mx-auto">
              {data.description}
            </p>
            
            {/* Token Info */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 max-w-md mx-auto">
              <div className="flex items-center justify-between mb-4">
                <span className="text-white/80 font-semibold">{data.tokenSymbol} Token</span>
                <DollarSign className="w-5 h-5 text-white/80" />
              </div>
              <div className="flex items-center gap-4">
                <div className="text-2xl font-bold text-white">{data.tokenPrice}</div>
                <div className={`flex items-center text-sm ${
                  data.tokenPriceChange >= 0 ? 'text-green-400' : 'text-red-400'
                }`}>
                  <TrendingUp className={`w-4 h-4 ${data.tokenPriceChange < 0 ? 'rotate-180' : ''}`} />
                  {Math.abs(data.tokenPriceChange)}%
                </div>
              </div>
              <div className="text-purple-300 text-sm mt-2">Total Supply: {data.totalSupply}</div>
            </div>
          </div>

          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <div className="bg-gradient-to-r from-purple-500/20 to-purple-600/20 backdrop-blur-sm rounded-xl p-6 border border-purple-400/30">
              <div className="flex items-center gap-3 mb-2">
                <Users className="w-6 h-6 text-purple-400" />
                <span className="text-purple-400 font-semibold">Active Members</span>
              </div>
              <div className="text-3xl font-bold text-white">{data.members.active.toLocaleString()}</div>
              <div className="text-purple-300 text-sm">of {data.members.total.toLocaleString()} total</div>
            </div>
            
            <div className="bg-gradient-to-r from-blue-500/20 to-blue-600/20 backdrop-blur-sm rounded-xl p-6 border border-blue-400/30">
              <div className="flex items-center gap-3 mb-2">
                <Vote className="w-6 h-6 text-blue-400" />
                <span className="text-blue-400 font-semibold">Active Proposals</span>
              </div>
              <div className="text-3xl font-bold text-white">{activeProposals.length}</div>
              <div className="text-blue-300 text-sm">{data.activity.proposalsThisMonth} this month</div>
            </div>
            
            <div className="bg-gradient-to-r from-green-500/20 to-green-600/20 backdrop-blur-sm rounded-xl p-6 border border-green-400/30">
              <div className="flex items-center gap-3 mb-2">
                <BarChart3 className="w-6 h-6 text-green-400" />
                <span className="text-green-400 font-semibold">Voting Power</span>
              </div>
              <div className="text-3xl font-bold text-white">{data.voting.totalVotingPower}</div>
              <div className="text-green-300 text-sm">{data.activity.averageParticipation}% participation</div>
            </div>
            
            <div className="bg-gradient-to-r from-yellow-500/20 to-yellow-600/20 backdrop-blur-sm rounded-xl p-6 border border-yellow-400/30">
              <div className="flex items-center gap-3 mb-2">
                <DollarSign className="w-6 h-6 text-yellow-400" />
                <span className="text-yellow-400 font-semibold">Treasury</span>
              </div>
              <div className="text-3xl font-bold text-white">{data.treasury.totalValue}</div>
              <div className="text-yellow-300 text-sm">{data.treasury.tokens.length} tokens</div>
            </div>
          </div>

          {/* Voting Parameters */}
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 mb-12">
            <h3 className="text-xl font-bold text-white mb-6">Voting Parameters</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-400 mb-2">{data.voting.quorum}%</div>
                <div className="text-white/80">Quorum Required</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-400 mb-2">{data.voting.votingPeriod}h</div>
                <div className="text-white/80">Voting Period</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-400 mb-2">{data.voting.minTokensToVote}</div>
                <div className="text-white/80">Min Tokens to Vote</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Proposals Section */}
      <section className="py-16 px-6 bg-black/20">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
            <h2 
              className="text-3xl font-bold"
              style={{ fontFamily: 'Georgia, serif' }}
            >
              Governance Proposals
            </h2>
            
            <div className="flex gap-4">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:ring-2 focus:ring-purple-400"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="passed">Passed</option>
                <option value="rejected">Rejected</option>
                <option value="executed">Executed</option>
              </select>
              
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:ring-2 focus:ring-purple-400"
              >
                <option value="all">All Types</option>
                <option value="treasury">Treasury</option>
                <option value="governance">Governance</option>
                <option value="technical">Technical</option>
                <option value="community">Community</option>
              </select>
            </div>
          </div>

          <div className="space-y-6">
            {filteredProposals.map((proposal) => (
              <div
                key={proposal.id}
                className={`bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 hover:border-purple-400/50 transition-all duration-300 ${
                  selectedProposal === proposal.id ? 'ring-2 ring-purple-400' : ''
                }`}
                onClick={() => setSelectedProposal(selectedProposal === proposal.id ? null : proposal.id)}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm font-semibold border ${getStatusColor(proposal.status)}`}>
                      {getStatusIcon(proposal.status)}
                      {proposal.status.charAt(0).toUpperCase() + proposal.status.slice(1)}
                    </div>
                    <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getTypeColor(proposal.type)}`}>
                      {proposal.type.charAt(0).toUpperCase() + proposal.type.slice(1)}
                    </span>
                  </div>
                  
                  <div className="text-right">
                    <div className="text-sm text-purple-300">Proposal #{proposal.id}</div>
                    <div className="text-xs text-white/60">by {proposal.proposer.slice(0, 6)}...{proposal.proposer.slice(-4)}</div>
                  </div>
                </div>
                
                <h3 className="text-xl font-bold text-white mb-3">{proposal.title}</h3>
                <p className="text-purple-200 mb-6 line-clamp-3">{proposal.description}</p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="bg-green-500/10 border border-green-400/20 rounded-lg p-3">
                    <div className="text-green-400 font-semibold mb-1">For</div>
                    <div className="text-white font-bold">{proposal.forVotes.toLocaleString()}</div>
                  </div>
                  <div className="bg-red-500/10 border border-red-400/20 rounded-lg p-3">
                    <div className="text-red-400 font-semibold mb-1">Against</div>
                    <div className="text-white font-bold">{proposal.againstVotes.toLocaleString()}</div>
                  </div>
                  <div className="bg-gray-500/10 border border-gray-400/20 rounded-lg p-3">
                    <div className="text-gray-400 font-semibold mb-1">Abstain</div>
                    <div className="text-white font-bold">{proposal.abstainVotes.toLocaleString()}</div>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 text-sm text-purple-300">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      Starts: {proposal.startDate}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      Ends: {proposal.endDate}
                    </span>
                  </div>
                  
                  {proposal.status === 'active' && (
                    <button className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg font-semibold transition-colors">
                      Vote Now
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Treasury Section */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 
            className="text-3xl font-bold mb-8"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            Treasury Overview
          </h2>
          
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20 mb-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-white">Total Treasury Value</h3>
              <div className="text-3xl font-bold text-green-400">{data.treasury.totalValue}</div>
            </div>
            
            <div className="space-y-4">
              {data.treasury.tokens.map((token, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold text-sm">{token.symbol.charAt(0)}</span>
                    </div>
                    <div>
                      <div className="font-semibold text-white">{token.symbol}</div>
                      <div className="text-purple-300 text-sm">{token.amount}</div>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <div className="font-bold text-white">{token.value}</div>
                    <div className="text-purple-300 text-sm">{token.percentage}%</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Members Section */}
      <section className="py-16 px-6 bg-black/20">
        <div className="max-w-6xl mx-auto">
          <h2 
            className="text-3xl font-bold mb-8"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            Community Members
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-gradient-to-r from-purple-500/20 to-purple-600/20 backdrop-blur-sm rounded-xl p-6 border border-purple-400/30">
              <div className="flex items-center gap-3 mb-2">
                <Users className="w-6 h-6 text-purple-400" />
                <span className="text-purple-400 font-semibold">Total Members</span>
              </div>
              <div className="text-2xl font-bold text-white">{data.members.total.toLocaleString()}</div>
            </div>
            
            <div className="bg-gradient-to-r from-blue-500/20 to-blue-600/20 backdrop-blur-sm rounded-xl p-6 border border-blue-400/30">
              <div className="flex items-center gap-3 mb-2">
                <Zap className="w-6 h-6 text-blue-400" />
                <span className="text-blue-400 font-semibold">Active Members</span>
              </div>
              <div className="text-2xl font-bold text-white">{data.members.active.toLocaleString()}</div>
            </div>
            
            <div className="bg-gradient-to-r from-green-500/20 to-green-600/20 backdrop-blur-sm rounded-xl p-6 border border-green-400/30">
              <div className="flex items-center gap-3 mb-2">
                <TrendingUp className="w-6 h-6 text-green-400" />
                <span className="text-green-400 font-semibold">New This Week</span>
              </div>
              <div className="text-2xl font-bold text-white">{data.members.newThisWeek}</div>
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
            <h3 className="text-xl font-bold text-white mb-6">Top Contributors</h3>
            <div className="space-y-4">
              {data.members.topContributors.map((member, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold text-sm">#{index + 1}</span>
                    </div>
                    <div>
                      <div className="font-semibold text-white">{member.name}</div>
                      <div className="text-purple-300 text-sm">{member.address.slice(0, 6)}...{member.address.slice(-4)}</div>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <div className="font-bold text-white">{member.contributions} contributions</div>
                    <div className="text-purple-300 text-sm">{member.votingPower} voting power</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black/50 backdrop-blur-sm border-t border-white/10 py-12 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <div className="flex justify-center gap-6 mb-6">
            <a href="#" className="text-gray-400 hover:text-purple-400 transition-colors">Discord</a>
            <a href="#" className="text-gray-400 hover:text-purple-400 transition-colors">Twitter</a>
            <a href="#" className="text-gray-400 hover:text-purple-400 transition-colors">GitHub</a>
            <a href="#" className="text-gray-400 hover:text-purple-400 transition-colors">Snapshot</a>
          </div>
          <p className="text-gray-400">
            &copy; 2024 {data.daoName}. Decentralized Autonomous Organization.
          </p>
        </div>
      </footer>
    </div>
  )
}
